<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells>
        <app-button @click.native="onShowModal(null)">Create rule</app-button>
      </app-cells>
      <app-cells>
        <app-input
          @input.native="onRuleSearch"
          v-model="filter_search"
          class="input--200"
          type="search"
          placeholder="Search"
          name="search"
        />
        <app-filters
          v-model="filter_active"
          :filters="filter_active_options"
          name="filterActive"
          @change="onChangeFilter"
        />
        <app-select
          v-model="filter_source"
          @input="onChangeFilter"
          :reduce="item => item.id"
          :options="source_options"
          placeholder="Source"
          class="select--200"
        />
        <app-select
          v-model="filter_account"
          @input="onChangeFilter"
          :reduce="item => item.id"
          :options="account_options"
          placeholder="Account"
          class="select--200"
        />
        <app-filters
          v-model="filter_apply"
          :filters="filter_apply_options"
          name="filterApply"
          @change="onChangeFilter"
        />
        <app-filters
          v-model="filter_applied"
          :filters="filter_applied_options"
          name="filterApplied"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__source><div class="table-default__left">Source</div></template>
        <template #h__account><div class="table-default__left">Account</div></template>
        <template #h__subtype><div class="table-default__left">Subtype</div></template>
        <template #h__description><div class="table-default__left">Description</div></template>
        <template #h__match_type><div class="table-default__left">Match Type</div></template>
        <template #h__pattern><div class="table-default__left">Pattern</div></template>
        <template #source="{ row }"><div class="table-default__left table-default__nowrap">{{ source_map[row.source] }}</div></template>
        <template #account="{ row }"><div class="table-default__left table-default__nowrap">{{ row.account?.name || '-' }}</div></template>
        <template #subtype="{ row }">
          <div class="table-default__left table-default__nowrap">{{ subtype_map[row.subtype]  }}</div>
        </template>
        <template #description="{ row }"><div class="table-default__left">{{ row.description || '-' }}</div></template>
        <template #match_type="{ row }"><div class="table-default__left">{{ match_type_map[row.match_type] }}</div></template>
        <template #pattern="{ row }"><div class="table-default__left">{{ row.pattern }}</div></template>
        <template #apply_to_history="{ row }">
          <div class="table-default__controls">
            <span
              class="status-tag"
              :style="{ 'backgroundColor': row.apply_to_history ? '#77b017' : '#5f5f5f' }"
            >
              {{ row.apply_to_history ? 'Apply' : 'Not apply' }}
            </span>
            <span
              v-if="row.apply_to_history"
              class="status-tag"
              :style="{ 'backgroundColor': row.history_applied ? '#77b017' : '#5f5f5f' }"
            >
              {{ row.history_applied ? 'Applied' : 'Not applied' }}
            </span>
          </div>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button v-if="row.editable" @click="onShowDeleteModal(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
            <button
              v-if="row.apply_to_history && !row.history_applied"
              @click="onShowApplyModal(row.id)"
              v-tooltip="{content: 'Apply to history', placement: 'top', offset: 10}"
              type="button"
            >
              <img src="@/assets/img/check-green-icon.svg" alt="Apply">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="apply-modal"
      :width="800"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      :reset="true"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button @click="$modal.hide('apply-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Apply to history</div>
        <div class="popup__body" style="min-height: 50px">
          <app-spinner v-if="!modal_rules_loaded" />
          <div v-else class="rule-table">
            <div class="rule-table__row">
              <span class="rule-table__head">Date</span>
              <span class="rule-table__head">Account</span>
              <span class="rule-table__head">Amount</span>
              <span class="rule-table__head">Reference</span>
              <span class="rule-table__head">Description</span>
            </div>
            <div v-for="row in modal_rules" :key="row.id" class="rule-table__row">
              <span class="rule-table__val">{{ dateFormat(row.timestamp) }}</span>
              <span class="rule-table__val">{{ row.account.name }}</span>
              <span class="rule-table__val">{{ amountFormat(row.amount) }}{{ row.account.currency.symbol }}</span>
              <span class="rule-table__val">{{ row.reference }}</span>
              <span class="rule-table__val">{{ row.description }}</span>
            </div>
          </div>
        </div>
        <app-cells v-if="modal_rules_loaded" position="center" :indent="false">
          <app-button @click.native="onApplyRules" type="button" ref="apply">Apply</app-button>
        </app-cells>
      </div>
    </modal>
    <modal
      name="rule-modal"
      :width="800"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      :reset="true"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button @click="$modal.hide('rule-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ item_to_action ? 'Update' : 'Create' }} rule</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="Source" required>
                  <app-select
                    v-model="form.source"
                    :options="source_options"
                    :reduce="item => item.id"
                    :readonly="item_to_action && !item_to_action.editable"
                    placeholder="Source"
                    :error="$v.form.source.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.source.$dirty && !$v.form.source.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Account">
                  <app-select
                    v-model="form.account"
                    :options="account_options"
                    :readonly="item_to_action && !item_to_action.editable"
                    placeholder="Account"
                  />
                </app-form-group>
                <app-form-group label="Subtype">
                  <app-select
                    v-model="form.subtype"
                    :options="subtype_options"
                    :reduce="item => item.id"
                    :readonly="item_to_action && !item_to_action.editable"
                    placeholder="Subtype"
                  />
                </app-form-group>
                <app-form-group label="Match Type" required>
                  <app-select
                    v-model="form.match_type"
                    :options="match_type_options"
                    :reduce="item => item.id"
                    :readonly="item_to_action && !item_to_action.editable"
                    placeholder="Match Type"
                    :error="$v.form.match_type.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.match_type.$dirty && !$v.form.match_type.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Pattern" required>
                  <app-textarea
                    v-model="form.pattern"
                    :readonly="item_to_action && !item_to_action.editable"
                    placeholder="Pattern"
                    :error="$v.form.pattern.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.pattern.$dirty && !$v.form.pattern.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Item">
                  <app-select
                    v-model="form.item"
                    :options="transaction_items_options"
                    :readonly="item_to_action && !item_to_action.editable"
                    placeholder="Item"
                  />
<!--                  <app-select-->
<!--                    v-if="form.subtype === 'income'"-->
<!--                    v-model="form.item"-->
<!--                    :options="transaction_items_options_income"-->
<!--                    label="name_revenue"-->
<!--                    placeholder="Item"-->
<!--                  />-->
<!--                  <app-select-->
<!--                    v-if="form.subtype === 'outcome'"-->
<!--                    v-model="form.item"-->
<!--                    :options="transaction_items_options_outcome"-->
<!--                    label="name_expense"-->
<!--                    placeholder="Item"-->
<!--                  />-->
                </app-form-group>
                <app-form-group label="Supplier">
                  <app-select
                    @search="onSupplierSearch"
                    v-model="form.supplier"
                    :options="supplier_options"
                    :filterBy="() => true"
                    :display-field="option => `${option.trade_name === option.legal_name ? option.trade_name : `${option.trade_name} (${option.legal_name})`}`"
                    :readonly="item_to_action && !item_to_action.editable"
                    label="trade_name"
                    placeholder="Supplier"
                  />
                </app-form-group>
                <app-form-group label="Priority">
                  <app-input
                    v-model.number="form.priority"
                    :readonly="item_to_action && !item_to_action.editable"
                    placeholder="Priority"
                    type="number"
                  />
                </app-form-group>
                <app-form-group label="Apply" class="form-group--center">
                  <app-cells>
                    <app-checkbox
                      v-model="form.active"
                      :readonly="item_to_action && !item_to_action.editable"
                      label="Active"
                    />
                    <app-checkbox
                      v-model="form.apply_to_history"
                      :readonly="item_to_action && !item_to_action.editable"
                      label="Apply to History"
                    />
                  </app-cells>
                </app-form-group>
                <app-form-group label="Description">
                  <app-textarea
                    v-model="form.description"
                    placeholder="Description"
                  />
                </app-form-group>
              </template>
            </app-grid>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit">{{ item_to_action ? 'Save' : 'Create' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete rule
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this rule?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteItem" ref="deleteItem">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getFinanceCashflowReconciliationRules,
  postFinanceCashflowReconciliationRules,
  patchFinanceCashflowReconciliationRules,
  deleteFinanceCashflowReconciliationRules,
  getAdministrativeSuppliers,
  getFinanceAccounts,
  getFinanceTransactionsItemsList,
  getFinanceCashflowReconciliationRulesPreview,
  postFinanceCashflowReconciliationRulesApply
} from '@/http'
import { format, parseISO } from 'date-fns'
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'FinanceCashflowReconciliationRuleEngine',
  data() {
    return {
      page_loaded: false,
      filter_search: '',
      filter_source: '',
      filter_account: '',
      filter_active: 'true',
      filter_apply: '',
      filter_applied: '',
      account_options: [],
      supplier_options: [],
      transaction_items_options: [],
      transaction_items_options_income: [],
      transaction_items_options_outcome: [],
      source_map: {
        wise: 'Wise',
        kassa: 'Kassa',
        stripe: 'Stripe',
        nmb: 'NMB',
        equity: 'Equity',
      },
      match_type_map: {
        startswith: 'Starts with',
        contains: 'Contains',
        equals: 'Equals',
        regex: 'Regex',
      },
      subtype_map: {
        income: 'Income',
        outcome: 'Outcome',
        revenue: 'Revenue',
        commission: 'Commission',
        refund: 'Refund',
        fee: 'Fee',
        payout: 'Payout',
        outcome_fee: 'Outcome fee',
      },
      match_type_options: [
        {name: 'Starts with', id: 'startswith'},
        {name: 'Contains', id: 'contains'},
        {name: 'Equals', id: 'equals'},
        {name: 'Regex', id: 'regex'},
      ],
      source_options: [
        {name: 'Wise', id: 'wise'},
        {name: 'Kassa', id: 'kassa'},
        {name: 'Stripe', id: 'stripe'},
        {name: 'NMB', id: 'nmb'},
        {name: 'Equity', id: 'equity'},
      ],
      subtype_options: [
        {name: 'Income', id: 'income'},
        {name: 'Outcome', id: 'outcome'},
        {name: 'Revenue', id: 'revenue'},
        {name: 'Commission', id: 'commission'},
        {name: 'Refund', id: 'refund'},
        {name: 'Fee', id: 'fee'},
        {name: 'Payout', id: 'payout'},
        {name: 'Outcome fee', id: 'outcome_fee'},
      ],
      filter_active_options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
        { value: '', label: 'All' },
      ],
      filter_apply_options: [
        { value: 'true', label: 'Apply' },
        { value: 'false', label: 'Not apply' },
        { value: '', label: 'All' },
      ],
      filter_applied_options: [
        { value: 'true', label: 'Applied' },
        { value: 'false', label: 'Not applied' },
        { value: '', label: 'All' },
      ],
      table: {
        items: [],
        columns: [
          'source',
          'account',
          'subtype',
          'priority',
          'description',
          'match_type',
          'pattern',
          'apply_to_history',
          'controls',
        ],
        options: {
          headings: {
            'source': 'Source',
            'account': 'Account',
            'subtype': 'Subtype',
            'priority': 'Priority',
            'description': 'Description',
            'match_type': 'Match Type',
            'pattern': 'Pattern',
            'apply_to_history': 'Apply to History',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      item_to_action: null,
      form: {},
      modal_rules: [],
      modal_rules_loaded: false,
    }
  },
  validations: {
    form: {
      source: { required },
      match_type: { required },
      pattern: { required },
    },
  },
  created() {
    Promise.all([
      getFinanceAccounts(),
      getFinanceTransactionsItemsList(),
      // getFinanceTransactionsItemsList({ expense: true }),
      // getFinanceTransactionsItemsList({ expense: true }),
      this.fetchItems()
    ])
      .then(response => {
        this.account_options = response[0].data
        this.transaction_items_options = response[1].data
        // this.transaction_items_options_income = response[1].data
        // this.transaction_items_options_outcome = response[2].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItems() {
      this.table_preloader = true
      const response = await getFinanceCashflowReconciliationRules({
        page: this.pagination.page,
        page_size: 20,
        search: this.filter_search,
        account: this.filter_search ? '' : this.filter_account,
        active: this.filter_search ? '' : this.filter_active,
        source: this.filter_search ? '' : this.filter_source,
        apply_to_history: this.filter_search ? '' : this.filter_apply,
        history_applied: this.filter_search ? '' : this.filter_applied,
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
    },
    onChangeFilter() {
      this.filter_search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      let data = {...this.form}
      if (data.supplier) data.supplier = data.supplier.id
      if (data.account) data.account = data.account.id
      if (data.item) data.item = data.item.id
      if (this.item_to_action) {
        if (!this.item_to_action.editable) data = {description: data.description}
        patchFinanceCashflowReconciliationRules(this.item_to_action.id, data)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Rule updated',
            })
            this.$modal.hide('rule-modal')
          })
      } else {
        postFinanceCashflowReconciliationRules(data)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Rule created',
            })
            this.$modal.hide('rule-modal')
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    onShowApplyModal(id) {
      this.modal_rules_loaded = false
      this.item_to_action = id
      getFinanceCashflowReconciliationRulesPreview(id)
        .then(response => {
          this.modal_rules = response.data
          this.modal_rules_loaded = true
        })
      this.$modal.show('apply-modal')
    },
    onApplyRules() {
      this.$refs.apply.preload = true
      postFinanceCashflowReconciliationRulesApply(this.item_to_action)
        .then(() => {
          this.fetchItems()
          this.$modal.hide('apply-modal')
        })
        .catch(() => { this.$refs.apply.preload = false })
    },
    onShowModal(row) {
      this.item_to_action = row
      if (this.item_to_action) {
        this.form = {
          source: this.item_to_action.source,
          account: this.item_to_action.account,
          subtype: this.item_to_action.subtype,
          match_type: this.item_to_action.match_type,
          pattern: this.item_to_action.pattern,
          description: this.item_to_action.description,
          item: this.item_to_action.item,
          supplier: this.item_to_action.supplier,
          priority: this.item_to_action.priority,
          active: this.item_to_action.active,
          apply_to_history: this.item_to_action.apply_to_history,
        }
      } else {
        this.form = {}
      }
      this.$v.form.$reset()
      this.$modal.show('rule-modal')
    },
    onShowDeleteModal(item_id) {
      this.item_to_action = item_id
      this.$modal.show('delete-modal')
    },
    onDeleteItem() {
      this.$refs.deleteItem.preload = true
      deleteFinanceCashflowReconciliationRules(this.item_to_action)
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Rule deleted',
          })
          this.$modal.hide('delete-modal')
        })
        .catch(() => {
          this.$refs.deleteItem.preload = false
        })
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
    onRuleSearch() {
      this.searchRule(this)
    },
    searchRule: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>

<style scoped lang="sass">
.rule-table
  margin-bottom: 20px

.rule-table__row
  display: grid
  grid-template-columns: 100px 100px 100px 1fr 1fr
  align-items: center
  padding: 7px 5px

  &:nth-child(odd)
    background-color: #f9f9f9

.rule-table__head
  font-weight: 500
</style>